import LinkButton from '@root/joinroot.com/src/components/buttons/link-button';
import LinkButtonModel from '@root/joinroot.com/src/models/link-button';
import PropTypes from '@root/vendor/prop-types';
import Responsive from '@root/core/src/utils/responsive';
import TileLayoutModel from '@root/joinroot.com/src/models/tile-layout';
import { Colors, StyleSheet } from '@root/core/src/utils/styles';
import { H2 } from '@root/joinroot.com/src/components/text';

export default function CategorySection({
  sectionTitle,
  viewAllButtonSlug,
  children,
  layout = TileLayoutModel.STACKED,
  backgroundColor = Colors.white(),
}) {
  const LAYOUT_STYLES_MAP = {
    SLIDING: slidingLayoutStyles,
    STACKED: stackedLayoutStyles,
  };

  const styles = LAYOUT_STYLES_MAP[layout];

  return (
    <section
      css={[styles.wrap, {
        backgroundColor,
      }]}
      data-testid={'category-section'}
    >
      {sectionTitle &&
        <H2
          css={styles.sectionTitle}
          data-testid={'section-title'}
        >
          {sectionTitle}
        </H2>
      }
      <div
        css={styles.tileLayout}
        data-testid={'tile-layout'}
      >
        {children}
        {layout === TileLayoutModel.SLIDING && <div css={styles.extraSliderMargin} />}
      </div>
      {viewAllButtonSlug &&
        <LinkButton
          buttonStyles={styles.viewAllButtonContainer}
          to={viewAllButtonSlug}
          type={LinkButtonModel.types.SECONDARY}
        >
          View all
        </LinkButton>
      }
    </section>
  );
}

CategorySection.propTypes = {
  backgroundColor: PropTypes.oneOf([Colors.white(), Colors.nearWhite()]),
  children: PropTypes.node.isRequired,
  layout: PropTypes.oneOf(Object.values(TileLayoutModel)),
  sectionTitle: PropTypes.string,
  viewAllButtonSlug: PropTypes.string,
};

const baseStyles = {
  wrap: {
    display: 'flex',
    alignItems: 'center',
    flexDirection: 'column',
  },
  sectionTitle: {
    margin: '0 20px 40px',
    textAlign: 'center',
  },
  viewAllButtonContainer: {
    margin: '10px 0 0',
  },
};

const stackedLayoutStyles = StyleSheet.create({
  ...baseStyles,
  tileLayout: {
    display: 'flex',
    flexWrap: 'wrap',
    maxWidth: 1184, // Visually evaluates to spec'd 1164 with 10px tile margins on either side.
    width: '100%',
  },
});

// Sliding layout designed to work optimally with 3-4 tiles.
const slidingLayoutStyles = StyleSheet.create({
  ...baseStyles,
  tileLayout: {
    display: 'flex',
    maxWidth: 1440,
    width: '100%',
    padding: '0 7%',
    scrollSnapType: 'x mandatory',
    overflowX: 'scroll',
    webkitOverflowScrolling: 'touch',
    // Hide Scrollbar: Every browser is different here...
    '&::-webkit-scrollbar': {
      display: 'none',
    },
    msOverflowStyle: 'none', // Not currently supported by the parser used in Emotion
    scrollbarWidth: 'none', // Current Firefox solution and likely the future web default solution
    ...Responsive.sm({
      padding: 0,
    }),
  },
  extraSliderMargin: {
    width: 30,
    flexShrink: 0,
    ...Responsive.sm({
      width: 1,
    }),
    ...Responsive.xl({
      display: 'none',
    }),
  },
});
